/**
 * Centralized Photo Reference System
 * All photos are hosted on Firebase Storage (yrd-2026.firebasestorage.app)
 * 
 * Usage:
 *   import { PHOTOS, getPhotoUrl, getMobileUrl } from "@/lib/photos";
 *   <img src={PHOTOS.CROWD.audienceHandsRaised} />
 *   
 * For mobile-optimized versions, append "_mobile" before extension:
 *   const mobileUrl = getMobileUrl(PHOTOS.CROWD.audienceHandsRaised);
 */

const FIREBASE_STORAGE_BASE = "https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/";

/**
 * Generates a Firebase Storage URL for a given filename
 */
export const getPhotoUrl = (filename: string): string => 
  `${FIREBASE_STORAGE_BASE}${encodeURIComponent(filename)}?alt=media`;

/**
 * Generates a mobile-optimized URL (appends _mobile before extension)
 */
export const getMobileUrl = (desktopUrl: string): string => {
  // Extract filename from URL, replace .webp with _mobile.webp
  const match = desktopUrl.match(/\/o\/(.+?)\?alt=media/);
  if (!match) return desktopUrl;
  
  const encodedFilename = match[1];
  const filename = decodeURIComponent(encodedFilename);
  const mobileFilename = filename.replace('.webp', '_mobile.webp');
  
  return `${FIREBASE_STORAGE_BASE}${encodeURIComponent(mobileFilename)}?alt=media`;
};

// ============================================
// PHOTO CATALOG - 46 Total Images
// ============================================

export const PHOTOS = {
  // Bar Shots (1)
  BAR: {
    threePeopleCaptainOutfits: getPhotoUrl("Yacht Rock Detroit_Bar Shot Three People in Captain Outfits.webp"),
  },

  // Boat Shots (3)
  BOAT: {
    groupSixPeoplePosing: getPhotoUrl("Yacht Rock Detroit_Boat Group Shot Six People Posing on Boat.webp"),
    groupSixPeopleWaving: getPhotoUrl("Yacht Rock Detroit_Boat Group Shot Six People Waving on Boat.webp"),
    captainPortraitLifePreserver: getPhotoUrl("Yacht Rock Detroit_Captain Portrait Man with Life Preserver.webp"),
  },

  // Couple Shots (2)
  COUPLE: {
    posingOnDock: getPhotoUrl("Yacht Rock Detroit_Couple Shot Posing on Dock by Marina.webp"),
    withDrinksAtNight: getPhotoUrl("Yacht Rock Detroit_Couple Shot with Drinks at Night.webp"),
  },

  // Crew (1)
  CREW: {
    sixStaffMatchingShirts: getPhotoUrl("Yacht Rock Detroit_Crew Group Shot Six Staff Matching Shirts Captain Hats.webp"),
  },

  // Crowd Shots (3)
  CROWD: {
    audienceHandsRaised: getPhotoUrl("Yacht Rock Detroit_Crowd Shot Audience Hands Raised Cheering.webp"),
    nightStageView: getPhotoUrl("Yacht Rock Detroit_Crowd Shot Night Stage View with Audience.webp"),
    sunsetStageView: getPhotoUrl("Yacht Rock Detroit_Crowd Shot Sunset View of Stage and Audience.webp"),
  },

  // DJ Shots (4)
  DJ: {
    djKeo: getPhotoUrl("Yacht Rock Detroit_DJ Shot DJ Keo with Captain Hat.webp"),
    nicNasty: getPhotoUrl("Yacht Rock Detroit_DJ Shot Nic Nasty at Turntables.webp"),
    pioneerTurntables: getPhotoUrl("Yacht Rock Detroit_DJ Shot at Pioneer Turntables.webp"),
    behindWithCrowd: getPhotoUrl("Yacht Rock Detroit_DJ Shot from Behind with Dancing Crowd.webp"),
  },

  // Dancing Shots (5)
  DANCING: {
    coupleSlowDancing: getPhotoUrl("Yacht Rock Detroit_Dancing Shot Couple Slow Dancing in Crowd.webp"),
    crowdCheeringCaptainHat: getPhotoUrl("Yacht Rock Detroit_Dancing Shot Crowd Cheering Hands Up Captain Hat.webp"),
    crowdHandsUpCaptainHats: getPhotoUrl("Yacht Rock Detroit_Dancing Shot Crowd Hands Up Captain Hats.webp"),
    womanWhiteDress: getPhotoUrl("Yacht Rock Detroit_Dancing Shot Woman in White Dress on Dance Floor.webp"),
    womenCheering: getPhotoUrl("Yacht Rock Detroit_Dancing Shot Women Cheering in Crowd.webp"),
  },

  // Female Only Shots (6)
  FEMALE_ONLY: {
    fourWomenNight: getPhotoUrl("Yacht Rock Detroit_Female Only Shot Four Women Night Roostertail.webp"),
    fourWomenRoostertail: getPhotoUrl("Yacht Rock Detroit_Female Only Shot Four Women at Roostertail.webp"),
    threeWomenDanceFloor: getPhotoUrl("Yacht Rock Detroit_Female Only Shot Three Women on Dance Floor.webp"),
    twoWomenPinkHair: getPhotoUrl("Yacht Rock Detroit_Female Only Shot Two Women Posing Pink Hair.webp"),
    twoWomenCaptainHats: getPhotoUrl("Yacht Rock Detroit_Female Only Shot Two Women in Captain Hats.webp"),
    twoWomenDrinks: getPhotoUrl("Yacht Rock Detroit_Female Only Shot Two Women with Drinks.webp"),
  },

  // Female Solo (1)
  FEMALE_SOLO: {
    tropicalShirt: getPhotoUrl("Yacht Rock Detroit_Female Solo Shot Woman in Tropical Shirt Posing.webp"),
  },

  // Group Shots (6)
  GROUP: {
    eightPeopleGazebo: getPhotoUrl("Yacht Rock Detroit_Group Shot Eight People Under Gazebo by Water.webp"),
    eightPeopleRoostertailSign: getPhotoUrl("Yacht Rock Detroit_Group Shot Eight People at Roostertail Sign.webp"),
    fiveFriendsRoostertail: getPhotoUrl("Yacht Rock Detroit_Group Shot Five Friends at Roostertail.webp"),
    fourFriendsDrinks: getPhotoUrl("Yacht Rock Detroit_Group Shot Four Friends with Drinks at Roostertail.webp"),
    fourPeopleHedge: getPhotoUrl("Yacht Rock Detroit_Group Shot Four People Green Hedge Backdrop.webp"),
    threeFriendsSign: getPhotoUrl("Yacht Rock Detroit_Group Shot Three Friends with Roostertail Sign.webp"),
  },

  // Guest (1)
  GUEST: {
    yellowShirtPier: getPhotoUrl("Yacht Rock Detroit_Guest Shot Man in Yellow Yacht Rock Shirt on Pier.webp"),
  },

  // Stage Shots (8)
  STAGE: {
    bandLargeOutdoor: getPhotoUrl("Yacht Rock Detroit_Stage Shot Band on Large Outdoor Stage.webp"),
    bandDramaticLights: getPhotoUrl("Yacht Rock Detroit_Stage Shot Band with Dramatic Lights.webp"),
    bandSpotlightBeams: getPhotoUrl("Yacht Rock Detroit_Stage Shot Band with Dramatic Spotlight Beams.webp"),
    fullBandCaptainHats: getPhotoUrl("Yacht Rock Detroit_Stage Shot Full Band Performing in Captain Hats.webp"),
    nighttimeOrangeLights: getPhotoUrl("Yacht Rock Detroit_Stage Shot Nighttime Orange Lights and Crowd.webp"),
    nighttimePurpleLights: getPhotoUrl("Yacht Rock Detroit_Stage Shot Nighttime Purple Lights and Crowd.webp"),
    pianistSparkle: getPhotoUrl("Yacht Rock Detroit_Stage Shot Pianist Singing with Sparkle Backdrop.webp"),
    hostsPerformersFistBump: getPhotoUrl("Yacht Rock Detroit_Stage Shot Two Hosts Performers Fist Bump.webp"),
  },

  // Three Men (1)
  THREE_MEN: {
    captainHatsCigars: getPhotoUrl("Yacht Rock Detroit_Three Men Shot Captain Hats and Cigars.webp"),
  },

  // Two Guys (2)
  TWO_GUYS: {
    hawaiianShirtsBlueHour: getPhotoUrl("Yacht Rock Detroit_Two Guys Shot Hawaiian Shirts Blue Hour.webp"),
    hawaiianShirtsSmiling: getPhotoUrl("Yacht Rock Detroit_Two Guys Shot Hawaiian Shirts Smiling.webp"),
  },

  // Venue (1)
  VENUE: {
    roostertailExteriorNight: getPhotoUrl("Yacht Rock Detroit_Venue Shot Roostertail Building Exterior Night.webp"),
  },
} as const;

// ============================================
// CURATED COLLECTIONS FOR COMMON USE CASES
// ============================================

/** Gallery Row 1 - Stage, crowd, and energy shots (20 images) */
export const GALLERY_ROW_1 = [
  PHOTOS.STAGE.bandDramaticLights,
  PHOTOS.CROWD.audienceHandsRaised,
  PHOTOS.STAGE.nighttimeOrangeLights,
  PHOTOS.DJ.behindWithCrowd,
  PHOTOS.CROWD.sunsetStageView,
  PHOTOS.STAGE.fullBandCaptainHats,
  PHOTOS.DANCING.crowdHandsUpCaptainHats,
  PHOTOS.STAGE.bandSpotlightBeams,
  PHOTOS.CROWD.nightStageView,
  PHOTOS.STAGE.nighttimePurpleLights,
  PHOTOS.DANCING.womenCheering,
  PHOTOS.STAGE.bandLargeOutdoor,
  PHOTOS.DJ.pioneerTurntables,
  PHOTOS.STAGE.pianistSparkle,
  PHOTOS.DANCING.crowdCheeringCaptainHat,
  PHOTOS.DJ.djKeo,
  PHOTOS.STAGE.hostsPerformersFistBump,
  PHOTOS.DJ.nicNasty,
  PHOTOS.DANCING.womanWhiteDress,
  PHOTOS.DANCING.coupleSlowDancing,
];

/** Gallery Row 2 - Social, group, and people shots (20 images) */
export const GALLERY_ROW_2 = [
  PHOTOS.GROUP.fiveFriendsRoostertail,
  PHOTOS.FEMALE_ONLY.fourWomenRoostertail,
  PHOTOS.COUPLE.withDrinksAtNight,
  PHOTOS.GROUP.fourFriendsDrinks,
  PHOTOS.FEMALE_ONLY.fourWomenNight,
  PHOTOS.BOAT.groupSixPeopleWaving,
  PHOTOS.GROUP.eightPeopleGazebo,
  PHOTOS.TWO_GUYS.hawaiianShirtsSmiling,
  PHOTOS.FEMALE_ONLY.threeWomenDanceFloor,
  PHOTOS.COUPLE.posingOnDock,
  PHOTOS.GROUP.eightPeopleRoostertailSign,
  PHOTOS.BAR.threePeopleCaptainOutfits,
  PHOTOS.FEMALE_ONLY.twoWomenCaptainHats,
  PHOTOS.THREE_MEN.captainHatsCigars,
  PHOTOS.GROUP.fourPeopleHedge,
  PHOTOS.FEMALE_ONLY.twoWomenDrinks,
  PHOTOS.BOAT.groupSixPeoplePosing,
  PHOTOS.TWO_GUYS.hawaiianShirtsBlueHour,
  PHOTOS.GROUP.threeFriendsSign,
  PHOTOS.FEMALE_ONLY.twoWomenPinkHair,
];

/** Legacy: Combined gallery for backwards compatibility */
export const GALLERY_PHOTOS = [...GALLERY_ROW_1.slice(0, 6), ...GALLERY_ROW_2.slice(0, 6)];

/** Hero section images - high energy stage/crowd shots */
export const HERO_PHOTOS = [
  PHOTOS.STAGE.bandDramaticLights,
  PHOTOS.CROWD.audienceHandsRaised,
  PHOTOS.STAGE.nighttimePurpleLights,
  PHOTOS.CROWD.sunsetStageView,
];

/** Social/friendly vibe photos */
export const SOCIAL_PHOTOS = [
  PHOTOS.GROUP.fiveFriendsRoostertail,
  PHOTOS.GROUP.fourFriendsDrinks,
  PHOTOS.FEMALE_ONLY.fourWomenRoostertail,
  PHOTOS.COUPLE.posingOnDock,
  PHOTOS.TWO_GUYS.hawaiianShirtsSmiling,
];

export default PHOTOS;
